import _ from "lodash";

import { UserWithRange } from "@/utils/types";
import { calculateOTEFromUser } from "lib/shared/compensation/calculateOTEFromUser";
import { annualizeCashValue } from "lib/shared/compensation/cash";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import { Company } from "prisma/cm/client";
import {
  getRangeBasePayLowMidHighCents,
  cellFormatterCurrency,
  getDoesRangeIncludeBonusTargetInCompaRatioCalculation,
  getIsCompanyOTE,
  getDoesCompanyIncludeBonusTargetInOTECalculation,
} from "utils/helpers";

export const calculateUserCompaRatio = ({
  user,
  company,
  convert,
  forceOTE,
}: {
  user: UserWithRange;
  company: Company;
  convert: any;
  forceOTE?: boolean;
}) => {
  if (!user.range) return null;

  const isOTE = forceOTE ?? getIsCompanyOTE(company);
  let includeBonusTarget = getDoesCompanyIncludeBonusTargetInOTECalculation(company);

  if (!getDoesRangeIncludeBonusTargetInCompaRatioCalculation(user.range)) {
    // range-level setting for bonus target overrides company-level setting
    includeBonusTarget = false;
  }

  let basePay = isOTE
    ? calculateOTEFromUser(user, company, includeBonusTarget)
    : annualizeCashValue({
        value: user.salary ?? 0,
        incomeFrequency: user.salaryIncomeFrequency,
        employmentSubType: user.employmentSubType as EmploymentSubType,
        weeklyWorkingHours: company.weeklyWorkingHours,
      });

  const { rangeBasePayMidCents } = getRangeBasePayLowMidHighCents(user.range, company, forceOTE);
  if (!rangeBasePayMidCents || _.isNil(basePay)) return null;

  const targetCurrencyCode = user.salaryCurrencyCode ?? undefined;

  const formatterParams = {
    decimalField: "salaryIncomeFrequency",
    currencyField: "salaryCurrencyCode",
    sourceCurrency: user.range.salaryCurrencyCode,
    targetCurrency: targetCurrencyCode,
  };

  const salaryMidConverted = cellFormatterCurrency(
    convert,
    // @ts-expect-error TS2345
    user.range,
    rangeBasePayMidCents,
    formatterParams,
  ).raw;

  const salaryMidConvertedAnnualized = annualizeCashValue({
    value: salaryMidConverted,
    incomeFrequency: user.range.salaryIncomeFrequency,
    employmentSubType: user.employmentSubType as EmploymentSubType,
  });

  return basePay / salaryMidConvertedAnnualized;
};
